import React, {useCallback, useEffect, useRef, useState} from "react";
import Select from "react-select";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import axios from "axios";
import apiUrl from "../../../shared/config";
import {AnimatePresence, motion} from "framer-motion";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import PresetModal from "../../UI/Modal/PresetModal/PresetModal";
import Auth from "../../../containers/Auth/Auth";
import Timer from "../../UI/Timer/Timer";
import {Tooltip} from "@material-ui/core";
import {Lock} from "@material-ui/icons";
import Spinner from "react-bootstrap/Spinner";
import SubmitButtonWarning from "../../UI/SubmitButtonWarning/SubmitButtonWarning";
import {
    cancelEditLock,
    getEditLock,
    LockResponses,
    LockType,
    overrideEditLock,
    requestLockUpdate
} from "../../../utils/lockUtils";


const changedText = {
    title: "Update Failed!",
    body: "The attempted update failed because this example game has been updated since you started editing. Please copy your changes and close the editing window."
}

const existsText = {
    title: "Update Failed!",
    body: "The attempted update failed because your lock to this example game has been overridden and the example game already exists on this pattern. Please copy your changes, close the editing window, and edit the example game if you have permission, otherwise feel free to leave a comment."
}

const unknownText = {
    title: "Update Failed!",
    body: "The attempted update failed for an unknown reason. Please copy your changes and close the editing window."
}

const overrideText = {
    title: "Override Editing Lock?",
    body: "Another user is currently editing this example game. If you choose to override this lock you may be overriding someone else's changes. If you don't want to override their lock please try again later."
}

const expiredText = {
    title: "Lock Expired",
    body: "Your suggested example game editing lock has expired. Please copy your changes and close the editing window."
}

export default function SuggestExampleGame({token, pattern_id, update, userId, patternGames, listOfGames, lastEditDate, designProblems}) {
    const [game, setGame] = useState({game_name: null, game_id: null})
    const [gameList, setGameList] = useState([]);
    const [gameDesc, setGameDesc] = useState("");
    const [gameDesignProblems, setGameDesignProblems] = useState([]);
    const [showInput, setShowInput] = useState(false);
    const [plusIsHovered, setPlusIsHovered] = useState(false);
    const [modalText, setModalText] = useState({title: "", body: ""});
    const modal = useRef();
    const overrideModal = useRef();
    const loginModal = useRef();
    const [mounted, setMounted] = useState(false);
    const [keyForTimer, setKeyForTimer] = useState(0);
    const [loading, setLoading] = useState(false)
    const [showFailMessage, setShowFailMessage] = useState(false)
    const [failMessage, setFailMessage] = useState('')

    const appearAndExit = {
        open: {
            height: 'auto', opacity: 1, transition: {
                duration: .5, ease: "easeInOut"
            },
        }, closed: {
            height: 0, opacity: 0, transition: {
                duration: .5, ease: "easeInOut"
            },
        },
    };

    const cancelLockPat = useCallback((patternId, id) => {
        if (token && patternId && id) {
            cancelEditLock(token, patternId, LockType.EXAMPLE_GAME, id)
                .then(response => {
                    console.log("Lock cancelled:", response);
                })
                .catch(() => {
                    console.log("Failed to cancel lock for suggested game.");
                });
        }
    }, [token]);

    // useEffect to track changes to pattern_id and page refreshes in order to get rid of open locks.
    useEffect(() => {
        const suggestedLocks = JSON.parse(localStorage.getItem('suggestedLocks'));

        if (suggestedLocks && (suggestedLocks.pattern_id !== pattern_id || !mounted)) {
            // Call cancelLockPAt with the old pattern_id and game_id
            cancelLockPat(suggestedLocks.pattern_id, suggestedLocks.game_id);
            setShowInput(false);
            setGame({game_id: null, game_name: null});
            setKeyForTimer(prev => ++prev);
        } else if (suggestedLocks && suggestedLocks.game_id && suggestedLocks.game_id !== game.game_id) { // this handles when you switch from one game to another.
            cancelLockPat(suggestedLocks.pattern_id, suggestedLocks.game_id)
            setKeyForTimer(prev => ++prev);
        }

        setMounted(true);
        // Update localStorage with the current pattern_id and game_id
        const currentLocks = { pattern_id, game_id: game.game_id };
        localStorage.setItem('suggestedLocks', JSON.stringify(currentLocks));
    }, [cancelLockPat, pattern_id, game.game_id, mounted]);



    useEffect(() => {
        const currentGames = patternGames.map(p => p.game_name);
        const list = listOfGames.filter(game => !currentGames.includes(game.label));
        setGameList(list);
    }, [listOfGames, patternGames]);

    function handleSubmit() {
        const data = {
            pattern_id: +pattern_id,
            example_description: gameDesc,
            design_problems: gameDesignProblems,
            game_id: game.game_id,
        };

        setLoading(true);
        let message = '';
        requestLockUpdate(token, pattern_id, LockType.EXAMPLE_GAME, lastEditDate, game.game_id)
            .then(
                response => {
                    if (response === LockResponses.LOCK_OWNED || response === LockResponses.LOCK_CREATED) {
                        axios.post(apiUrl + "Pattern/setGame?token=" + token, data)
                            .then(response => {
                                if (!response.data.message) {
                                    console.log('Post successful:', response.data);
                                    setShowInput(false);
                                    setShowFailMessage(false)
                                } else {
                                    setShowFailMessage(true);
                                    message = response.data.message;
                                    setFailMessage(response.data.message);
                                }
                            })
                            .then(() => {
                                if (!message) {
                                    cancelLock(game.game_id) }
                            })
                            .then(() => {
                                if (!message) {
                                    setGame({game_name: null, game_id: null});
                                    setGameDesc("")
                                    setGameDesignProblems([])
                                    update();
                                }
                                setLoading(false)
                            })
                            .catch(error => {
                                console.error('There was an error!', error);
                                setModalText(unknownText);
                                setLoading(false)
                                modal.current.open();
                            });
                    } else if (response === LockResponses.LOCK_EXISTS) {
                        setModalText(overrideText);
                        setLoading(false)
                        overrideModal.current.open();
                    } else if (response === LockResponses.UNSEEN_CHANGES) {
                        setModalText(existsText);
                        setLoading(false)
                        modal.current.open();
                    } else {
                        setModalText(changedText);
                        setLoading(false)
                        modal.current.open();
                    }
                }).catch(() => {
            setModalText(unknownText);
            setLoading(false);
            modal.current.open();
        })
    }

    function handleOpenEdit() {
        if (showInput) {
            setShowInput(false);
            game.game_id && cancelLock(game.game_id);
            setGame({game_name: null, game_id: null});
        } else if (token) {
            setShowInput(true)
        } else {
            loginModal.current.open();
        }
    }

    function requestLockStatus(id) {
        if (token) {
            getEditLock(token, pattern_id, LockType.EXAMPLE_GAME, id)
                .then(
                    response => {
                        if (response === LockResponses.LOCK_EXISTS) {
                            setModalText(overrideText);
                            overrideModal.current.open();
                        }
                    }).catch(() => {
                setModalText(unknownText);
                modal.current.open();
            })
        } else {
            loginModal.current.open();
        }
    }

    function overrideLock() {
        if (token && game.game_id) {
            setLoading(true);
            overrideEditLock(token, pattern_id, LockType.EXAMPLE_GAME, game.game_id)
                .then(response => {
                    console.log(response);
                    if (response === LockResponses.LOCK_CREATED) {
                        setShowInput(true);
                    }
                    setLoading(false);
                })
                .catch(() => console.log("Edit Failed due to inability to override lock"))
        } else {
            console.log("hello this should not happen")
        }
    }

    function cancelLock(id) {
        if (token) {
            cancelEditLock(token, pattern_id, LockType.EXAMPLE_GAME, id)
                .then(response => console.log(response))
                .catch(() => console.log("Failed to cancel lock for suggested game."))
        }
    }

    function handleOverride() {
        overrideLock();
        setKeyForTimer(prev => ++prev);
        overrideModal.current.close();
    }

    const problemOptions =  designProblems && designProblems.length > 1 ?
        designProblems.map((problem, index) => ({ value: problem.design_problem_id, label: "Design Problem " + (index + 1) }))
        : []


    return (<>
        <hr style={{
            border: "none", borderTop: "1px solid black"
        }}
        />
        <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            margin: "0 auto 10px"
        }}>
            <h3 style={{
                fontSize: "18px",
                fontWeight: "bold",
            }}>Suggest An Example Game:</h3>
            <AddCircleOutlineIcon
                size="sm"
                onMouseEnter={() => setPlusIsHovered(true)}
                onMouseLeave={() => setPlusIsHovered(false)}
                style={{color: plusIsHovered ? "#00bd00" : "green", transform: plusIsHovered ? 'scale(1.2)' : 'scale(1)', marginRight: "calc(2%)"}}
                onClick={handleOpenEdit}
            />
        </div>
        <AnimatePresence>
            {showInput && (
                <motion.div
                    variants={appearAndExit}
                    initial="closed"
                    animate="open"
                    exit="closed"
                    style={{overflow: "hidden"}}
                >
                    <Select
                        // the styling on this is so cursed, but it works. The motion.div needs the overflow to be hidden,
                        // but that would hide some of the select so... now we have this.
                        className="example-games-select-full"
                        value={game.game_id ? {value: game.game_id, label: game.game_name} : null}
                        placeholder="Select a Game"
                        options={gameList}
                        onChange={(sel) => {
                            setGame({game_name: sel.label, game_id: sel.value})
                            requestLockStatus(sel.value);
                        }}
                        styles={{
                            menuPortal: (base) => ({...base, marginTop: -75, zIndex: 500, textAlign: "left"}), // Ensure proper zIndex and adjust position with margin.
                        }}
                        menuPortalTarget={document.body} // Attach to body to prevent overflow issues
                        menuPosition="absolute" // Ensures dropdown stays positioned relative to input
                        menuShouldScrollIntoView={false} // Prevent unwanted scroll behavior
                    />
                    <div className="example-description" style={{marginBottom: 5}}>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="How is this pattern applied in this game?"
                            value={game.example_description}
                            onChange={(event) => setGameDesc(event.target.value)}
                        />
                    </div>
                    {designProblems && designProblems.length > 1
                        ? <Select
                            className="example-games-select-full"
                            isMulti={true}
                            menuPortalTarget={document.body} // Attach to body to prevent overflow issues
                            menuPosition="absolute" // Ensures dropdown stays positioned relative to input
                            menuShouldScrollIntoView={false} // Prevent unwanted scroll behavior
                            styles={{
                                menuPortal: (base) => ({...base, marginTop: -75, zIndex: 500, textAlign: "left"}), // Ensure proper zIndex and adjust position with margin.
                            }}
                            defaultValue={designProblems && designProblems.length > 0 && game.design_problems && game.design_problems.length > 0
                                ? game.design_problems.map(order_id => {
                                    return problemOptions.find(option => option.value === order_id)
                                        || ''
                                }) : ''}
                            placeholder="Which design problems are solved by this example?"
                            options={problemOptions}
                            onChange={(val) => setGameDesignProblems(val ? val.map(e => e.value) : [])}
                        /> : ''}
                    <SubmitButtonWarning
                        token={token} userId={userId}
                        handleSubmit={handleSubmit}
                        failMessage={failMessage}
                        setShowFailMessage={setShowFailMessage}
                        showFailMessage={showFailMessage}
                        postType={{
                            lowercase: "example games",
                            capital: "Example Game",
                            capitalLowercase: "Example games",
                        }}
                        submitButton={
                            <Button
                                onClick={() => handleSubmit()}
                                className="edit-pattern-button"
                                style={{marginTop: 10}}
                            >
                                Submit Example
                            </Button>
                        }
                    />

                    {game.game_id && <span key={"timerIndex: " + keyForTimer} style={{
                        color: "gray", float: "right", display: "inline-flex", justifyContent: "center",
                        alignItems: "center", marginRight: "16px", marginTop: "16px"
                    }}>
                        <Tooltip title="Example Locked for Editing" placement="top">
                            <Lock/>
                        </Tooltip>
                        <Timer
                            style={{marginBottom: 0, marginLeft: "5px"}}
                            hours={0} minutes={59} seconds={59} onComplete={() => {
                            setModalText(expiredText);
                            modal.current.open();
                        }}
                        />
                    </span>}
                    {loading && <Spinner animation={"border"} style={{float: "right", scale: .5, marginRight: "16px", marginTop: "16px"}}/>}
                </motion.div>
            )}
        </AnimatePresence>

        <PresetModal ref={modal} title={modalText.title} body={modalText.body}/>
        <PresetModal buttonText="Override" onButtonPress={handleOverride} ref={overrideModal} onClose={() => setGame({
            game_id: null,
            game_name: null
        })} title={modalText.title} body={modalText.body}/>
        <PresetModal ref={loginModal} title="Login to Suggest an Example Game"><Auth shouldRedirect
                                                                                     hideDragon/></PresetModal>
    </>);

}